import React, { PureComponent } from 'react'

const DELAY = 400
const INACTIVE_TIME = 5000

type HoverHandlerChildrenProps = {
  hovering: boolean
  focused: boolean
  intent: boolean
  active: boolean
  props: {
    onMouseEnter(): void
    onMouseLeave(): void
    onMouseMove(): void
  }
}

type HoverHandlerProps = {
  children: (props: HoverHandlerChildrenProps) => React.ReactElement
  nowrap?: boolean
  onChange?: (state: Partial<State>) => void
}

type State = {
  hovering: boolean
  focused: boolean
  intent: boolean
  active: boolean
}

export default class HoverHandler extends PureComponent<HoverHandlerProps> {
  state = {
    hovering: false,
    focused: false,
    intent: false,
    active: false,
  }

  timer?: NodeJS.Timeout

  activeTimer?: NodeJS.Timeout

  componentWillUnmount() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = undefined
    }
    if (this.activeTimer) {
      clearTimeout(this.activeTimer)
      this.activeTimer = undefined
    }
  }

  onChange = (state: Partial<State>) => {
    this.setState(state)
    if (this.props.onChange) this.props.onChange(state)
  }

  onEnter = () => {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = undefined
    }

    this.onChange({ hovering: true })

    this.timer = setTimeout(() => this.onChange({ intent: true }), DELAY)
  }

  onLeave = () => {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = undefined
    }

    this.onChange({ hovering: false })

    this.timer = setTimeout(() => this.onChange({ intent: false }), DELAY)
  }

  onFocus = () => {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = undefined
    }

    this.onChange({ focused: true })

    this.timer = setTimeout(() => this.onChange({ intent: true }), DELAY)
  }

  onBlur = (e: React.FocusEvent) => {
    // Do not lose focus if focus moved inside the same parent
    if (e.currentTarget.contains(e.relatedTarget as Node)) return

    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = undefined
    }

    this.onChange({ focused: false })

    this.timer = setTimeout(() => this.onChange({ intent: false }), DELAY)
  }

  onMove = () => {
    if (this.activeTimer) {
      clearTimeout(this.activeTimer)
      this.activeTimer = undefined
    }

    this.onChange({ active: true })

    this.activeTimer = setTimeout(
      () => this.onChange({ active: false }),
      INACTIVE_TIME,
    )
  }

  render() {
    const { children, nowrap } = this.props

    const { focused, hovering, intent, active } = this.state

    const content = children({
      focused,
      hovering,
      intent,
      active,
      props: {
        onMouseEnter: this.onEnter,
        onMouseLeave: this.onLeave,
        onMouseMove: this.onMove,
      },
    })

    if (nowrap) {
      return content
    }

    return (
      <span
        onMouseEnter={this.onEnter}
        onMouseLeave={this.onLeave}
        onMouseMove={this.onMove}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
      >
        {content}
      </span>
    )
  }
}
